import { UnexpectedErrorAlert, ChangeEmailConfirmLinkExpiredAlert } from '@/components/alerts';
import { AccountCenterBox } from '@/layout/AccountCenterBox';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import { api } from '@/services/api';
import { ChangeEmailConfirmResponse, ChangeEmailConfirmResultEnum } from '@shared/types';
import { AxiosResponse } from 'axios';

enum FormStatus {
  NoAlert = 'NoAlert',
  ChangeEmailConfirmLinkExpired = 'ChangeEmailConfirmLinkExpiredAlert',
  UnexpectedError = 'UnexpectedErrorAlert',
}

export const ChangeEmailConfirm = () => {
  useDocumentTitle('Marshall Reddick - Change Email Confirmation');

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') ?? '';
  const email = searchParams.get('email') ?? '';

  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(FormStatus.NoAlert);

  const hasSubmittedRef = useRef(false);

  const alertMap: Partial<Record<FormStatus, React.ReactNode>> = {
    [FormStatus.ChangeEmailConfirmLinkExpired]: <ChangeEmailConfirmLinkExpiredAlert />,
    [FormStatus.UnexpectedError]: <UnexpectedErrorAlert />,
  };

  useEffect(() => {
    if (!token || !email) {
      navigate('/login', { replace: true });
      return;
    }

    if (hasSubmittedRef.current) return;
    hasSubmittedRef.current = true;

    setLoading(true);

    api
      .post('/auth/change-email-confirm', { token, email })
      .then((response: AxiosResponse<ChangeEmailConfirmResponse>) => {
        const { result } = response.data;

        if (result === ChangeEmailConfirmResultEnum.SUCCESS) {
          window.location.replace(import.meta.env.VITE_WEBSITE_URL);
          return;
        }

        if (result === ChangeEmailConfirmResultEnum.LINK_EXPIRED) {
          setStatus(FormStatus.ChangeEmailConfirmLinkExpired);
        } else {
          setStatus(FormStatus.UnexpectedError);
        }
      })
      .catch((error) => {
        const status = error.response?.status;
        if (status && status >= 400 && status < 500) {
          navigate('/login', { replace: true });
          return;
        }
        setStatus(FormStatus.UnexpectedError);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  return (
    <AccountCenterBox isSubmitting={loading}>
      <div className="min-h-[10rem] md:min-h-[15rem]">{alertMap[status]}</div>
    </AccountCenterBox>
  );
};
